import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../utils/api";
import AdBanner from "./AdBanner";

const AD_EVERY = 5; // Afficher un AdBanner toutes les N publications

const MOOD_KEYS = [
  { value: "neutral", emoji: "🌿", key: "mood_neutral" },
  { value: "happy", emoji: "✨", key: "mood_happy" },
  { value: "sad", emoji: "🌧", key: "mood_sad" },
  { value: "anxious", emoji: "🌀", key: "mood_anxious" },
  { value: "grateful", emoji: "🤍", key: "mood_grateful" },
  { value: "angry", emoji: "🔥", key: "mood_angry" },
  { value: "hopeful", emoji: "🌅", key: "mood_hopeful" },
];

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

// ─────────────────────────────────────────────────────────────
//  CommentSection — section commentaires toggleable par post
// ─────────────────────────────────────────────────────────────
function CommentSection({ postId, initialCount = 0, currentUser }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/api/comments/${postId}`);
      setComments(data.comments || []);
      setCount(data.comments?.length || 0);
    } catch (err) {
      console.error("comments load error:", err);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  function toggle() {
    if (!open) {
      setOpen(true);
      loadComments();
      // Focus input after animation
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      setOpen(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      const data = await api(`/api/comments/${postId}`, {
        method: "POST",
        body: JSON.stringify({ content: text.trim() }),
      });
      setComments(prev => [...prev, data.comment]);
      setCount(prev => prev + 1);
      setText("");
    } catch (err) {
      console.error("comment post error:", err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(commentId) {
    try {
      await api(`/api/comments/${commentId}`, { method: "DELETE" });
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("comment delete error:", err);
    }
  }

  return (
    <div className="mt-1">
      {/* Toggle button */}
      <button
        onClick={toggle}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all duration-200 ${
          open
            ? "bg-sensoria-accent/10 text-sensoria-accent-light"
            : "text-sensoria-muted hover:bg-sensoria-surface hover:text-sensoria-text-dim"
        }`}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span>{count === 1 ? "1 commentaire" : count > 1 ? `${count} commentaires` : "Commenter"}</span>
        <svg className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Comments panel */}
      {open && (
        <div className="mt-3 space-y-3 rounded-xl border border-sensoria-border/30 bg-sensoria-bg/40 p-3">
          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
            </div>
          )}

          {/* Empty */}
          {!loading && comments.length === 0 && (
            <p className="py-2 text-center text-xs text-sensoria-muted">Soyez le premier à commenter</p>
          )}

          {/* Comments list */}
          {comments.map((c) => (
            <div key={c.id} className="group flex gap-2.5">
              {/* Avatar */}
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sensoria-accent/10 text-xs font-semibold text-sensoria-accent">
                {(c.username || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-white">{c.username || "Anonyme"}</span>
                  <span className="text-[10px] text-sensoria-muted">{timeAgo(c.created_at)}</span>
                  {/* Delete own comment */}
                  {currentUser?.id === c.user_id && (
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="ml-auto opacity-0 transition-opacity group-hover:opacity-100 text-sensoria-muted hover:text-sensoria-danger"
                      title="Supprimer"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="mt-0.5 text-xs leading-relaxed text-sensoria-text">{c.content}</p>
              </div>
            </div>
          ))}

          {/* Input */}
          {currentUser && (
            <form onSubmit={handleSubmit} className="flex gap-2 pt-1">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sensoria-accent/15 text-xs font-semibold text-sensoria-accent">
                {(currentUser?.username || "?")[0].toUpperCase()}
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-sensoria-border/40 bg-sensoria-surface/50 px-3 py-1 focus-within:border-sensoria-accent/40 transition-colors">
                <input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Ajouter un commentaire…"
                  maxLength={1000}
                  className="min-w-0 flex-1 bg-transparent text-xs text-white placeholder-sensoria-muted outline-none"
                />
                <button
                  type="submit"
                  disabled={!text.trim() || submitting}
                  className="shrink-0 rounded-full p-1 text-sensoria-accent transition-all hover:bg-sensoria-accent/10 disabled:opacity-30"
                >
                  {submitting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Feed principal
// ─────────────────────────────────────────────────────────────
export default function Feed() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const MOODS = MOOD_KEYS.map((m) => ({ ...m, label: t(m.key) }));
  const [posts, setPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("neutral");
  const [posting, setPosting] = useState(false);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [error, setError] = useState("");
  const [moderationWarning, setModerationWarning] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    try {
      const data = await api("/api/posts");
      setPosts(data.posts || []);
    } catch (err) {
      console.error("Feed load error:", err);
    } finally {
      setLoadingFeed(false);
    }
  }, []);

  const fetchMyLikes = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api("/api/likes/my");
      setLikedPosts(new Set(data.liked_posts || []));
    } catch (err) {
      console.error("Likes load error:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
    fetchMyLikes();
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, [fetchPosts, fetchMyLikes]);

  async function handleImageUpload(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("entity_type", "post_image");
    fd.append("entity_id", user?.id || "shared");
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/photos/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok && data.url) setImageUrl(data.url);
      else setError("Erreur lors de l'upload de l'image.");
    } catch { setError("Erreur upload."); }
    finally { setUploading(false); }
  }

  async function handlePost(e) {
    e.preventDefault();
    if (!content.trim() || posting) return;

    setPosting(true);
    setError("");
    setModerationWarning("");

    try {
      const data = await api("/api/posts", {
        method: "POST",
        body: JSON.stringify({ content: content.trim(), mood, image_url: imageUrl || undefined }),
      });
      setContent("");
      setMood("neutral");
      setImageUrl("");
      setPosts((prev) => [data.post, ...prev]);
    } catch (err) {
      if (err.status === 403) {
        setModerationWarning(err.data?.detail || t("feed_moderation_warning"));
      } else {
        setError(err.message);
      }
    } finally {
      setPosting(false);
    }
  }

  async function handleLike(postId) {
    try {
      const data = await api(`/api/likes/${postId}`, { method: "POST" });
      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (data.liked) next.add(postId);
        else next.delete(postId);
        return next;
      });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? { ...p, like_count: (p.like_count || 0) + (data.liked ? 1 : -1) }
            : p
        )
      );
    } catch (err) {
      console.error("Like error:", err);
    }
  }

  async function handleDelete(postId) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api(`/api/posts/${postId}`, { method: "DELETE" });
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="space-y-4">
      {/* ── Composer ── */}
      <div className="sensoria-card sensoria-glow">
        <form onSubmit={handlePost}>
          <textarea
            value={content}
            onChange={(e) => { setContent(e.target.value); setModerationWarning(""); }}
            placeholder={t("feed_placeholder")}
            rows={3}
            maxLength={2000}
            className="sensoria-input resize-none text-sm"
          />
          {/* Mood selector — horizontal scroll on mobile */}
          <div className="mt-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 scrollbar-hide">
            {MOODS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMood(m.value)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  mood === m.value
                    ? "border-sensoria-accent/50 bg-sensoria-accent/10 text-sensoria-accent-light"
                    : "border-sensoria-border text-sensoria-text-dim hover:border-sensoria-muted"
                }`}
              >
                <span>{m.emoji}</span>
                <span className="hidden sm:inline">{m.label}</span>
              </button>
            ))}
          </div>

          {/* Aperçu image attachée */}
          {imageUrl && (
            <div className="relative mt-2 inline-block">
              <img src={imageUrl} alt="Aperçu" className="max-h-48 rounded-lg object-cover" />
              <button
                type="button"
                onClick={() => setImageUrl("")}
                className="absolute top-1 right-1 rounded-full bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-red-700"
              >✕</button>
            </div>
          )}

          {moderationWarning && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
              ⚠️ {moderationWarning}
            </div>
          )}
          {error && (
            <div className="mt-3 rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-4 py-3 text-sm text-sensoria-danger">
              {error}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {/* Bouton upload image */}
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploading}
                title="Ajouter une photo"
                className="flex items-center gap-1.5 rounded-lg border border-sensoria-border px-2.5 py-1.5 text-xs text-sensoria-text-dim hover:border-sensoria-accent hover:text-sensoria-accent disabled:opacity-50"
              >
                {uploading ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent inline-block" />
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                <span className="hidden sm:inline">{imageUrl ? "Changer" : "Photo"}</span>
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => e.target.files[0] && handleImageUpload(e.target.files[0])}
              />
              <span className="text-xs text-sensoria-muted">{content.length}/2000</span>
            </div>
            <button type="submit" disabled={posting || !content.trim()} className="sensoria-btn px-5 py-2 text-sm">
              {posting ? (
                <span className="flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <span className="hidden sm:inline">{t("feed_analyzing")}</span>
                  <span className="sm:hidden">…</span>
                </span>
              ) : t("feed_publish")}
            </button>
          </div>
        </form>
      </div>


      {/* ── Posts ── */}
      {loadingFeed ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
        </div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center">
          <div className="mb-3 text-4xl">🌿</div>
          <p className="text-sensoria-text-dim">{t("feed_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post, i) => {
            const moodData = MOODS.find((m) => m.value === post.mood) || MOODS[0];
            const isLiked = likedPosts.has(post.id);
            return (
              <React.Fragment key={post.id}>
              {/* AdBanner toutes les AD_EVERY publications (pas en première position) */}
              {i > 0 && i % AD_EVERY === 0 && <AdBanner key={`ad-${i}`} />}
              <article
                className={`sensoria-card group animate-fade-in ${post.is_spotlighted ? "ring-1 ring-amber-500/40 shadow-lg shadow-amber-500/10" : ""}`}
                style={{ animationDelay: `${i * 40}ms` }}
              >
                {post.is_spotlighted && (
                  <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                    <span>⭐</span> <span>Spotlight</span>
                  </div>
                )}

                {/* Post header */}
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sensoria-accent/15 text-sm font-semibold text-sensoria-accent">
                      {(post.username || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-white">{post.username || "Anonyme"}</span>
                      <div className="flex items-center gap-1.5 text-xs text-sensoria-muted">
                        <span>{timeAgo(post.created_at)}</span>
                        <span>·</span>
                        <span>{moodData.emoji}</span>
                        <span className="hidden sm:inline">{moodData.label}</span>
                      </div>
                    </div>
                  </div>
                  {user?.id === post.user_id && (
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="shrink-0 rounded-lg p-1.5 text-sensoria-muted opacity-0 transition-all hover:bg-sensoria-danger/10 hover:text-sensoria-danger group-hover:opacity-100"
                      title="Supprimer"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Post content */}
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-sensoria-text">{post.content}</p>

                {/* Image attachée */}
                {post.image_url && (
                  <div className="mt-3 overflow-hidden rounded-xl">
                    <img
                      src={post.image_url}
                      alt="Image du post"
                      className="max-h-80 w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Actions bar: likes + comments */}
                <div className="mt-3 flex items-center gap-1 border-t border-sensoria-border/30 pt-3">
                  {/* Like */}
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all duration-200 ${
                      isLiked
                        ? "bg-pink-500/10 text-pink-400"
                        : "text-sensoria-muted hover:bg-sensoria-surface hover:text-pink-400"
                    }`}
                  >
                    <svg className="h-4 w-4" fill={isLiked ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{post.like_count || 0}</span>
                  </button>

                  {/* Comments toggle */}
                  <CommentSection
                    postId={post.id}
                    initialCount={post.comment_count || 0}
                    currentUser={user}
                  />
                </div>
              </article>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
